import { type FC, type KeyboardEvent, useCallback, useState } from "react";

import type { OrgChannelSummary } from "../blackboard/useOrgChannels";

import { RetroError } from "../blackboard/retro";
import { Button } from "../common/Button";
import { Modal } from "../common/Modal";
import { normalizeRoomName, useRoomCreation } from "./useRoomCreation";

interface NewRoomDialogProps {
  onClose: () => void;
  /** Called with the created shared channel once creation succeeds. */
  onCreated: (channel: OrgChannelSummary) => void;
}

/**
 * Create a new shared channel (name only for now — env / actor / repo steps can
 * layer on later). Creation goes through the shared `useRoomCreation` primitive
 * so this dialog and the Spawn picker share one implementation.
 */
export const NewRoomDialog: FC<NewRoomDialogProps> = ({
  onClose,
  onCreated,
}) => {
  const { createRoom, creating, error, clearError } = useRoomCreation();
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);

  const slug = normalizeRoomName(name);
  const nameInvalid = slug.length === 0;
  const showNameError = touched && nameInvalid;

  const submit = useCallback(async () => {
    setTouched(true);
    if (normalizeRoomName(name).length === 0) {
      return;
    }
    try {
      const channel = await createRoom(name);
      onCreated(channel);
      onClose();
    } catch {
      // Error surfaced via the hook's `error`; keep the dialog open.
    }
  }, [name, createRoom, onCreated, onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void submit();
      }
    },
    [submit],
  );

  return (
    <Modal
      title="New Channel"
      subtitle="Create a shared channel where actors collaborate"
      onClose={onClose}
      widthClass="max-w-sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={creating}
            loadingText="Creating…"
            onClick={() => void submit()}
          >
            Create Channel
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error && <RetroError message={error} />}
        <div>
          <label
            htmlFor="new-room-name"
            className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400"
          >
            Channel name <span className="text-rose-500">*</span>
          </label>
          <input
            id="new-room-name"
            type="text"
            autoFocus
            value={name}
            aria-invalid={showNameError}
            aria-describedby={showNameError ? "new-room-name-error" : undefined}
            onChange={(e) => {
              setName(e.target.value);
              if (error) {
                clearError();
              }
            }}
            onBlur={() => setTouched(true)}
            onKeyDown={handleKeyDown}
            placeholder="duo-review"
            className={`w-full border bg-slate-50 px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-1 dark:bg-slate-900/60 dark:text-white dark:placeholder:text-slate-600 dark:focus:bg-slate-900 ${
              showNameError
                ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500/30 dark:border-rose-500/60"
                : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/30 dark:border-slate-700 dark:focus:border-emerald-400/60 dark:focus:ring-emerald-400/30"
            }`}
          />
          {showNameError ? (
            <p
              id="new-room-name-error"
              className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-rose-500"
            >
              A channel name is required.
            </p>
          ) : (
            slug.length > 0 &&
            slug !== name.trim() && (
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-600">
                Will be created as #{slug}
              </p>
            )
          )}
        </div>
      </div>
    </Modal>
  );
};
